export type TimerHandle = {
  id: number
  cancel: () => void
}

export type IntervalHandle = {
  cancel: () => void
}

export type LoopHandle = {
  cancel: () => void
}

const canUseWorker = () => typeof Worker !== 'undefined' && typeof URL !== 'undefined' && typeof Blob !== 'undefined'

class BackgroundScheduler {
  private nextId = 1

  // 单个共享的“多路复用”定时器 worker：用 set/clear 消息管理任意多个定时器。
  // 历史实现是每个 setTimer/sleep 都 new Worker(blob:)、用完 terminate，导致高频
  // 场景（如 device_model 每条命令都起一个超时守卫、收到回包就 cancel）持续刷出
  // blob: script 请求。改为单 worker 后，cancel 只是一条 clear 消息，零 worker 创建/销毁。
  private timerWorker: Worker | null = null
  private timerWorkerFailed = false
  private readonly timerCallbacks = new Map<number, () => void>()

  sleep(ms: number): Promise<void> {
    if (ms <= 0) {
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      const handle = this.setTimer(resolve, ms)
      void handle
    })
  }

  setTimer(callback: () => void, ms: number): TimerHandle {
    const delayMs = Math.max(0, Math.floor(ms))
    const id = this.nextId++

    const worker = this.getTimerWorker()
    if (worker) {
      this.timerCallbacks.set(id, callback)
      worker.postMessage({ type: 'set', id, ms: delayMs })
      return {
        id,
        cancel: () => {
          // 取消只需通知 worker 清除该定时器，不销毁 worker（无 churn）。
          if (this.timerCallbacks.delete(id)) {
            worker.postMessage({ type: 'clear', id })
          }
        },
      }
    }

    const timeoutId = setTimeout(callback, delayMs)
    return {
      id,
      cancel: () => clearTimeout(timeoutId),
    }
  }

  clearTimer(handle: TimerHandle | null | undefined) {
    handle?.cancel()
  }

  setInterval(callback: () => void, ms: number): IntervalHandle {
    return this.setLoop(() => {
      callback()
      return ms
    }, ms)
  }

  setLoop(callback: () => number | null | Promise<number | null>, initialMs: number): LoopHandle {
    const worker = this.createLoopWorker()
    if (worker) {
      let active = true
      worker.onmessage = async () => {
        if (!active) {
          return
        }

        const nextMs = await callback()
        if (!active || nextMs === null) {
          active = false
          worker.terminate()
          return
        }

        worker.postMessage({ ms: Math.max(0, Math.floor(nextMs)) })
      }
      worker.onerror = (event) => {
        console.warn('background scheduler loop worker failed:', event.message)
        active = false
        worker.terminate()
      }
      worker.postMessage({ ms: Math.max(0, Math.floor(initialMs)) })

      return {
        cancel: () => {
          active = false
          worker.terminate()
        },
      }
    }

    let active = true
    let timer: TimerHandle | null = null
    const tick = async () => {
      if (!active) {
        return
      }

      const nextMs = await callback()
      if (!active || nextMs === null) {
        return
      }

      timer = this.setTimer(tick, nextMs)
    }
    timer = this.setTimer(tick, initialMs)

    return {
      cancel: () => {
        active = false
        timer?.cancel()
        timer = null
      },
    }
  }

  // 懒创建并缓存唯一的多路复用定时器 worker。它在 worker 线程里用 setTimeout 管理
  // 多个定时器，避免主线程后台标签页节流；不再用 Atomics.wait（那会阻塞线程、
  // 无法在单 worker 内多路复用）。需要更高精度的周期性动画仍由 setLoop 的专用 worker 承担。
  private getTimerWorker(): Worker | null {
    if (this.timerWorker) {
      return this.timerWorker
    }
    if (this.timerWorkerFailed || !canUseWorker()) {
      return null
    }
    try {
      const code = `
        const timers = new Map();
        self.onmessage = (event) => {
          const data = event.data || {};
          if (data.type === 'set') {
            const id = data.id;
            if (typeof id !== 'number') return;
            const ms = Math.max(0, Number(data.ms) || 0);
            const handle = setTimeout(() => { timers.delete(id); self.postMessage({ id }); }, ms);
            timers.set(id, handle);
          } else if (data.type === 'clear') {
            const handle = timers.get(data.id);
            if (handle !== undefined) { clearTimeout(handle); timers.delete(data.id); }
          }
        };
      `
      const blob = new Blob([code], { type: 'application/javascript' })
      const workerUrl = URL.createObjectURL(blob)
      const worker = new Worker(workerUrl)
      URL.revokeObjectURL(workerUrl)
      worker.onmessage = (event) => {
        const id = (event.data as { id?: number } | null)?.id
        if (typeof id !== 'number') return
        const callback = this.timerCallbacks.get(id)
        if (callback) {
          this.timerCallbacks.delete(id)
          callback()
        }
      }
      worker.onerror = (event) => {
        console.warn('background scheduler worker failed:', event.message)
        // 共享 worker 异常（极罕见）：降级到主线程定时器，并将已在途的回调尽快触发（与旧
        // 实现的 onerror -> setTimeout(callback, 0) 行为一致），之后的定时器均走 setTimeout。
        this.timerWorkerFailed = true
        this.timerWorker = null
        const pending = Array.from(this.timerCallbacks.values())
        this.timerCallbacks.clear()
        for (const callback of pending) {
          setTimeout(callback, 0)
        }
      }
      this.timerWorker = worker
      return worker
    } catch (error) {
      console.warn('background scheduler unavailable:', error)
      this.timerWorkerFailed = true
      return null
    }
  }

  private createLoopWorker(): Worker | null {
    if (!canUseWorker()) {
      return null
    }

    try {
      const code = `
        const sleep = (ms, done) => {
          if (typeof SharedArrayBuffer !== 'undefined' && typeof Atomics !== 'undefined' && typeof Atomics.wait === 'function') {
            const lock = new Int32Array(new SharedArrayBuffer(4));
            Atomics.wait(lock, 0, 0, ms);
            done();
            return;
          }
          setTimeout(done, ms);
        };

        self.onmessage = (event) => {
          const ms = Math.max(0, Number(event.data?.ms) || 0);
          sleep(ms, () => self.postMessage({ ok: true }));
        };
      `
      const blob = new Blob([code], { type: 'application/javascript' })
      const workerUrl = URL.createObjectURL(blob)
      const worker = new Worker(workerUrl)
      URL.revokeObjectURL(workerUrl)
      return worker
    } catch (error) {
      console.warn('background scheduler loop unavailable:', error)
      return null
    }
  }
}

export const backgroundScheduler = new BackgroundScheduler()
